import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  CharterAdministratorDto,
  CharterClauseDto,
  CharterGovernanceBody,
  CharterGovernanceDto,
  CharterLegalNature,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CharterAdministratorItemComponent } from '@features/dashboard/company/corporate-charter/components/charter-administrator-item/charter-administrator-item.component';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import { CharterSigningGuideComponent } from '@features/dashboard/company/corporate-charter/components/charter-signing-guide/charter-signing-guide.component';
import { dominantSource } from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import {
  ADMINISTRATION_MODEL_LABEL,
  GOVERNANCE_BODY_LABEL,
  isCorporation,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/** Ordem dos órgãos na tela: conselho antes da diretoria (S.A.), administração (Ltda) primeiro. */
const BODY_ORDER: CharterGovernanceBody[] = [
  'administration',
  'board-of-directors',
  'executive-board',
  'fiscal-council',
  'other',
];

/**
 * Administração e assinatura (Ltda) ou administração e representação (S.A.). No topo, "Quem acionar para assinar"
 * (`app-charter-signing-guide`): por situação, quem chamar e em que combinação. Abaixo, os administradores (conselho e
 * diretoria, na S.A.), cada um com a procedência e o histórico. Ex-administradores ficam na aba própria da página.
 */
@Component({
  selector: 'app-charter-governance-card',
  imports: [
    CharterAdministratorItemComponent,
    CharterProvenanceComponent,
    CharterSigningGuideComponent,
  ],
  templateUrl: './charter-governance-card.component.html',
  styleUrl: './charter-governance-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterGovernanceCardComponent {
  readonly governance = input.required<CharterGovernanceDto>();
  /** Cláusulas de crédito: quórum exigido para garantia, dívida e oneração aparece no "quem assina". */
  readonly clauses = input<CharterClauseDto[]>([]);
  readonly legalNature = input<CharterLegalNature | null>(null);
  readonly currency = input('BRL');

  protected readonly isCorporation = computed(() => isCorporation(this.legalNature()));

  protected readonly title = computed(() =>
    this.isCorporation() ? 'Administração e representação' : 'Administração e assinatura'
  );

  protected readonly model = computed(() => {
    const value = this.governance().administrationModel.value;
    return value ? ADMINISTRATION_MODEL_LABEL[value] : null;
  });

  /** Fonte dominante: o ato de onde vem a maioria dos administradores e das regras de assinatura. */
  protected readonly source = computed(() => {
    const g = this.governance();
    return dominantSource([...g.administrators, ...g.signingRules]);
  });

  /** Administradores agrupados por órgão, na ordem de `BODY_ORDER`. */
  protected readonly bodies = computed(() => {
    const groups = new Map<CharterGovernanceBody, CharterAdministratorDto[]>();
    for (const admin of this.governance().administrators) {
      const list = groups.get(admin.body) ?? [];
      list.push(admin);
      groups.set(admin.body, list);
    }
    return BODY_ORDER.filter(body => groups.has(body)).map(body => ({
      body,
      label: GOVERNANCE_BODY_LABEL[body],
      administrators: groups.get(body) ?? [],
    }));
  });
}
